import { useEffect, useMemo, useState } from 'react';
import { Loader2, Search, Forward, Check } from 'lucide-react';
import { Modal } from '../../../components/Modal';
import { Avatar } from '../../../components/Avatar';
import { useToast } from '../../../components/Toast';
import { atendimentoApi } from '../lib/api';
import { formatTelefoneBR } from '@fiodecobre/shared';
import { useListaTeclado } from '../../../lib/useListaTeclado';
import type { Conversa, Inbox, Mensagem } from '../lib/tipos';

interface Props {
  aberto: boolean;
  mensagens: Mensagem[];
  /** Inbox da conversa de onde as mensagens sairam. Quem abre o modal sabe disso;
   *  sem ela o modal so podia CHUTAR pela assinatura (ver `inboxOrigem` abaixo). */
  inbox?: Inbox;
  onFechar: () => void;
}

function previewMsg(m: Mensagem): string {
  if (m.tipo === 'image') return m.texto ? `Foto: ${m.texto}` : 'Foto';
  if (m.tipo === 'video') return m.texto ? `Video: ${m.texto}` : 'Video';
  if (m.tipo === 'document') return m.midia_nome ? `Documento: ${m.midia_nome}` : 'Documento';
  if (m.tipo === 'audio') return 'Audio';
  if (m.tipo === 'location') return m.texto ? `Localizacao: ${m.texto}` : 'Localizacao';
  return m.texto ?? '';
}

export function ModalEncaminhar({ aberto, mensagens, inbox, onFechar }: Props) {
  const toast = useToast();
  const [conversas, setConversas] = useState<Conversa[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [busca, setBusca] = useState('');
  const [selecionadas, setSelecionadas] = useState<Set<number>>(new Set());

  // Caixa por onde o encaminhamento SAI = a aba em que a conversa esta aberta (prop `inbox`).
  // Mensagem recebida do cliente nao tem assinatura, entao o palpite antigo pela assinatura
  // caia sempre em 'atendimento' — e encaminhar de dentro de Compras oferecia a conversa de
  // Atendimento do mesmo numero, ou seja, saia pelo WhatsApp errado. A assinatura fica so
  // como fallback pra chamada que nao informar a inbox.
  // ⚠️ Encaminhar nao existe na caixa `app` — la nao ha transporte pra fora, e o
  // backend recusa (RDS chamados 1.27). O modal nem chega a ser oferecido nessa
  // aba; se chegar, cai no fallback e a rota devolve o motivo por alvo.
  const inboxOrigem: Inbox = useMemo(() => {
    if (inbox) return inbox;
    const assin = (mensagens[0]?.assinatura ?? '').toLowerCase();
    if (assin.includes('compras')) return 'compras';
    return 'atendimento';
  }, [inbox, mensagens]);

  useEffect(() => {
    if (!aberto) return;
    setBusca('');
    setSelecionadas(new Set());
    let cancelado = false;
    setCarregando(true);
    // Pede ja filtrado pela caixa: senao o teto de 500 se divide entre as duas inboxes
    // e conversa antiga da caixa certa cai fora do recorte.
    atendimentoApi.listarConversas({ limit: 500, inbox: inboxOrigem })
      .then((lista) => { if (!cancelado) setConversas(lista); })
      .catch(() => toast.erro('Falha ao carregar conversas'))
      .finally(() => { if (!cancelado) setCarregando(false); });
    return () => { cancelado = true; };
  }, [aberto, inboxOrigem, toast]);

  const filtradas = useMemo(() => {
    // So conversas da MESMA caixa — o destino define por qual numero a mensagem sai.
    // Dedup por telefone segue valendo (defensivo: mesmo numero repetido na inbox).
    const porChave = new Map<string, Conversa>();
    for (const c of conversas) {
      if (c.inbox !== inboxOrigem) continue;
      const ct = c.contato;
      const chave = (ct.telefone ?? '').replace(/\D/g, '') || ct.jid;
      if (!chave) continue;
      if (!porChave.has(chave)) porChave.set(chave, c);
    }
    const lista = [...porChave.values()];

    const q = busca.trim().toLowerCase();
    if (!q) return lista;
    return lista.filter((c) => {
      const ct = c.contato;
      return (
        (ct.nome ?? '').toLowerCase().includes(q) ||
        (ct.push_name ?? '').toLowerCase().includes(q) ||
        (ct.telefone ?? '').includes(q) ||
        ct.jid.includes(q)
      );
    });
  }, [busca, conversas, inboxOrigem]);

  const teclado = useListaTeclado(filtradas, (c) => alternar(c.id));

  function alternar(id: number) {
    setSelecionadas((prev) => {
      const novo = new Set(prev);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });
  }

  async function encaminhar() {
    if (mensagens.length === 0 || selecionadas.size === 0 || enviando) return;
    setEnviando(true);
    const alvos = Array.from(selecionadas);
    let okTotal = 0;
    let falhaTotal = 0;
    let primeiroErro = '';
    try {
      // Encaminha cada mensagem (na ordem do chat) pros alvos selecionados.
      for (const m of mensagens) {
        try {
          const r = await atendimentoApi.encaminharMensagem(m.id, alvos);
          okTotal += r.resultados.filter((x) => x.ok).length;
          const fs = r.resultados.filter((x) => !x.ok);
          falhaTotal += fs.length;
          if (!primeiroErro && fs[0]?.erro) primeiroErro = fs[0].erro;
        } catch (e: any) {
          falhaTotal += alvos.length;
          if (!primeiroErro) primeiroErro = e?.message ?? 'erro de rede';
        }
      }
      if (okTotal > 0 && falhaTotal === 0) {
        toast.sucesso(mensagens.length === 1 && alvos.length === 1 ? 'Mensagem encaminhada' : `Encaminhado (${okTotal} envio${okTotal > 1 ? 's' : ''})`);
        onFechar();
      } else if (okTotal > 0) {
        toast.aviso(`${okTotal} enviado(s), ${falhaTotal} falharam`);
        onFechar();
      } else {
        toast.erro(primeiroErro || 'Falha ao encaminhar');
      }
    } finally {
      setEnviando(false);
    }
  }

  if (!aberto || mensagens.length === 0) return null;

  return (
    <Modal
      aberto={aberto}
      onFechar={enviando ? () => {} : onFechar}
      titulo={mensagens.length > 1 ? `Encaminhar ${mensagens.length} mensagens` : 'Encaminhar mensagem'}
      largura={520}
      rodape={
        <>
          <button type="button" className="btn-secundario" onClick={onFechar} disabled={enviando}>Cancelar</button>
          <button
            type="button"
            className="btn-primario flex items-center gap-1.5"
            onClick={encaminhar}
            disabled={selecionadas.size === 0 || enviando}
          >
            {enviando ? <Loader2 size={14} className="animate-spin" /> : <Forward size={14} />}
            {enviando ? 'Encaminhando...' : `Encaminhar${selecionadas.size > 0 ? ` (${selecionadas.size})` : ''}`}
          </button>
        </>
      }
    >
      <div className="mb-3 p-2.5 rounded-lg" style={{ backgroundColor: 'rgba(0,0,0,0.04)', border: '1px solid var(--border, rgba(0,0,0,0.08))' }}>
        <div className="flex items-center justify-between gap-2 mb-0.5">
          <div className="text-[11px] uppercase tracking-wide opacity-60">{mensagens.length > 1 ? `${mensagens.length} mensagens` : 'Mensagem'}</div>
          {/* Por onde sai — a UI informa o canal em vez de deixar adivinhar (o numero e outro em cada caixa). */}
          <span className="text-[11px] font-bold px-1.5 py-0.5 rounded" style={{ background: 'var(--accent)', color: 'var(--accent-fg)' }}>
            Sai por {inboxOrigem === 'compras' ? 'Compras' : 'Atendimento'}
          </span>
        </div>
        <div className="text-sm whitespace-pre-wrap break-words space-y-0.5 max-h-24 overflow-y-auto">
          {mensagens.map((m) => <div key={m.id} className="line-clamp-1">{mensagens.length > 1 ? '• ' : ''}{previewMsg(m)}</div>)}
        </div>
      </div>

      <div className="relative mb-2">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 opacity-50" />
        <input
          type="text"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          onKeyDown={teclado.onKeyDown}
          placeholder="Buscar contato, telefone..."
          className="w-full pl-8 pr-3 py-1.5 rounded border text-sm bg-white/5 outline-none focus:ring-2 focus:ring-blue-400"
          style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))', fontSize: '16px' }}
        />
      </div>

      {carregando ? (
        <div className="flex items-center justify-center py-8 opacity-70">
          <Loader2 size={18} className="animate-spin" />
        </div>
      ) : filtradas.length === 0 ? (
        <div className="text-center py-8 opacity-60 text-sm">
          Nenhuma conversa em {inboxOrigem === 'compras' ? 'Compras' : 'Atendimento'}{busca.trim() ? ' para essa busca' : ''}.
        </div>
      ) : (
        <div ref={teclado.listaRef} className="space-y-1 max-h-[55vh] overflow-y-auto pr-1">
          {filtradas.map((c, idx) => {
            const ct = c.contato;
            const selecionada = selecionadas.has(c.id);
            const titulo = [ct.nome, ct.sobrenome].filter(Boolean).join(' ') || ct.push_name || ct.telefone || ct.jid;
            const destaque = idx === teclado.highlight;
            return (
              <button
                key={c.id}
                data-idx={idx}
                type="button"
                onClick={() => alternar(c.id)}
                onMouseEnter={() => teclado.setHighlight(idx)}
                className="w-full flex items-center gap-2.5 p-2 rounded-lg text-left transition"
                style={{
                  backgroundColor: selecionada ? 'var(--positive-bg)' : destaque ? 'var(--primary-soft)' : 'transparent',
                  border: destaque ? '1px solid var(--primary)' : '1px solid var(--border, rgba(0,0,0,0.08))',
                }}
              >
                <Avatar nome={titulo} fotoUrl={ct.foto_url} tamanho={32} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate" style={{ color: selecionada ? '#000' : 'var(--page-fg, currentColor)' }}>
                    {titulo}
                  </div>
                  <div className="text-[11px] truncate opacity-70" style={{ color: selecionada ? '#000' : undefined }}>
                    {/* Pela peca de exibicao: a coluna guarda so' digitos ([DA-708]). */}
                    {formatTelefoneBR(ct.telefone || ct.jid.replace(/@.*/, ''))}
                  </div>
                </div>
                {selecionada && <Check size={16} className="text-black flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
